import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';
import { useSourceExportEntitlement } from '@/hooks/useSourceExportEntitlement';
import type { WriterVersionSummary } from '@/components/writer/WriterVersionScrubber';

interface Props {
  /** The version the currently displayed PDF was built from. */
  version: WriterVersionSummary | null;
  /** Re-runs the compile for entitled users so the credit line disappears. */
  onRecompile?: (v: WriterVersionSummary) => void | Promise<void>;
  busy?: boolean;
}

/**
 * Transparency chip for the free-plan PaperVerse credit line printed in the
 * footer of every page. Free users see what the PDF carries and how to remove
 * it; entitled users (paid / admin) who still hold a marked PDF get a
 * no-credit-cost recompile.
 */
const WriterFreeMarkChip: React.FC<Props> = ({ version, onRecompile, busy = false }) => {
  const { allowed, loading } = useSourceExportEntitlement();
  if (!version?.marked || loading) return null;

  return (
    <span
      className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border/60 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground"
      title="Free plan PDFs carry a small “Made with PaperVerse” credit line in the page footer. Paid plans compile without it."
    >
      <Sparkles className="h-3 w-3" aria-hidden />
      PaperVerse credit
      {allowed && onRecompile ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onRecompile(version)}
          className="underline underline-offset-2 hover:text-foreground disabled:opacity-50"
          title="Recompiles this version without the credit line. Recompiles cost no credits."
        >
          {busy ? 'Recompiling…' : 'Remove'}
        </button>
      ) : (
        <Link to="/pricing-v2" className="underline underline-offset-2 hover:text-foreground">
          Upgrade
        </Link>
      )}
    </span>
  );
};

export default WriterFreeMarkChip;
